import { ForbiddenError } from '@/domain/errors/forbidden-error'
import { Account } from '@/domain/usecases'
import { FeatureFlagType, FeatureFlagClient, getFlagValue } from '@/infra/feature-flag'
import {
  makeSkillWebManagerUsersRedirectUrl,
  makeSkillWebRequestsRedirectUrl
} from '@/main/factories/http'
import { LDClient } from 'launchdarkly-react-client-sdk'
import { redirect } from 'react-router-dom'
import { PathConstants } from '../paths'

// ============================================
// TYPES
// ============================================

export type GuardContext = {
  account: Account.Model
  ldClient?: LDClient
}

export type GuardResult =
  | { allowed: true }
  | { allowed: false; redirect?: string; error?: Error; reason: string }

export type Guard = (ctx: GuardContext) => GuardResult

// ============================================
// ROLE-BASED GUARDS
// ============================================

/**
 * Requires user to be a Company Manager
 */
export function requireCompanyManager(ctx: GuardContext): GuardResult {
  if (Account.isCompanyManager(ctx.account)) {
    return { allowed: true }
  }

  console.log('[Guard] User is not a company manager')
  return {
    allowed: false,
    redirect: PathConstants.Routes.HOME.value,
    reason: 'User is not a company manager'
  }
}

/**
 * Requires user to be an Internal Company Manager
 */
export function requireInternalCompanyManager(ctx: GuardContext): GuardResult {
  if (Account.isInternalCompanyManager(ctx.account)) {
    return { allowed: true }
  }

  console.log('[Guard] User is not an internal company manager')
  return {
    allowed: false,
    redirect: PathConstants.Routes.HOME.value,
    reason: 'User is not an internal company manager'
  }
}

/**
 * Requires user to have a Pass Plan
 * If user is company manager without plan, redirects to appropriate page
 */
export function requirePassPlan(ctx: GuardContext): GuardResult {
  if (Account.hasPassPlan(ctx.account)) {
    return { allowed: true }
  }

  console.log('[Guard] User does not have pass plan')

  // Special handling for company managers without plan
  if (Account.isInternalCompanyManager(ctx.account)) {
    return {
      allowed: false,
      redirect: makeSkillWebRequestsRedirectUrl(),
      reason: 'Internal company manager without pass plan - redirect to requests'
    }
  }

  if (Account.isCompanyManager(ctx.account)) {
    return {
      allowed: false,
      redirect: makeSkillWebManagerUsersRedirectUrl(),
      reason: 'Company manager without pass plan - redirect to manage users'
    }
  }

  // Regular user without plan - throw error
  return {
    allowed: false,
    error: new ForbiddenError(
      'Para ter acesso ao benefício de educação, solicite ao responsável de sua empresa.',
      'Você ainda não possuí plano cadastrado'
    ),
    reason: 'User does not have pass plan'
  }
}

/**
 * Requires user to have completed onboarding
 */
export function requireOnboardingCompleted(ctx: GuardContext): GuardResult {
  if (ctx.account.onboardingCompleted !== false) {
    return { allowed: true }
  }

  console.log('[Guard] Onboarding not completed')
  return {
    allowed: false,
    redirect: PathConstants.Routes.ONBOARDING.value,
    reason: 'Onboarding not completed'
  }
}

/**
 * Requires user to have access to onboarding
 * User must have a pass plan and onboarding must not be completed
 */
export function requireOnboardingAccess(ctx: GuardContext): GuardResult {
  const hasPassPlan = Account.hasPassPlan(ctx.account)
  const onboardingNotCompleted = ctx.account.onboardingCompleted === false

  if (hasPassPlan && onboardingNotCompleted) {
    return { allowed: true }
  }

  console.log('[Guard] User cannot access onboarding')
  return {
    allowed: false,
    redirect: PathConstants.Routes.HOME.value,
    reason: 'User does not have access to onboarding (needs pass plan and incomplete onboarding)'
  }
}

// ============================================
// FEATURE FLAG GUARDS
// ============================================

/**
 * Requires a feature flag to be enabled
 * Waits for LaunchDarkly to be ready and uses default value if not ready
 */
export function requireFeatureFlag(flag: FeatureFlagType, ctx: GuardContext): GuardResult {
  // Wait for LaunchDarkly to be fully initialized before checking feature flags
  // This prevents race conditions where loaders execute before flags are loaded
  if (ctx.ldClient && !FeatureFlagClient.isReady()) {
    // Use default value if LD is not ready yet
    const allowed = flag.defaultValue as boolean
    if (!allowed) {
      console.warn('[Guard] LaunchDarkly not ready, using default value (denied)')
      return {
        allowed: false,
        redirect: PathConstants.Routes.HOME.value,
        reason: 'Feature flag not ready, using default value (denied)'
      }
    }
    console.warn('[Guard] LaunchDarkly not ready, using default value (allowed)')
    return { allowed: true }
  }

  const allowed = getFlagValue(flag, ctx.ldClient)
  if (allowed) {
    return { allowed: true }
  }

  console.log(`[Guard] Feature flag ${flag.name} is disabled`)
  return {
    allowed: false,
    redirect: PathConstants.Routes.HOME.value,
    reason: `Feature flag ${flag.name} is disabled`
  }
}

// ============================================
// GUARD COMPOSITION
// ============================================

/**
 * Composes multiple guards into a single guard
 * All guards must pass for the composed guard to pass
 * Returns the first guard failure if any guard fails
 */
export function composeGuards(...guards: Guard[]): Guard {
  return (ctx: GuardContext): GuardResult => {
    for (const guard of guards) {
      const result = guard(ctx)
      if (!result.allowed) {
        return result
      }
    }
    return { allowed: true }
  }
}

// ============================================
// GUARD EXECUTION
// ============================================

/**
 * Executes a guard and returns a redirect Response if access is denied
 * Returns null if access is allowed
 * Throws error if guard returns an error
 */
export function executeGuard(guard: Guard, ctx: GuardContext): Response | null {
  const result = guard(ctx)

  if (result.allowed) {
    return null
  }

  // If guard returned an error, throw it
  if (result.error) {
    throw result.error
  }

  // Otherwise redirect
  const redirectUrl = result.redirect || PathConstants.Routes.HOME.value
  console.log(`[Guard] Redirecting to ${redirectUrl}: ${result.reason}`)
  return redirect(redirectUrl)
}

// ============================================
// COMMON GUARD COMBINATIONS
// ============================================

export const guards = {
  /**
   * Requires user to be a company manager
   */
  companyManager: requireCompanyManager,

  /**
   * Requires user to be an internal company manager
   */
  internalCompanyManager: requireInternalCompanyManager,

  /**
   * Requires user to have a pass plan and completed onboarding
   * This is the most common guard for regular authenticated routes
   */
  passPlanHolder: composeGuards(requirePassPlan, requireOnboardingCompleted),

  /**
   * Requires user to have access to onboarding
   * User must have pass plan and onboarding not completed
   */
  onboardingAccess: requireOnboardingAccess,

  /**
   * Requires user to be a company manager with a specific feature flag
   */
  companyManagerWithFeature: (flag: FeatureFlagType): Guard =>
    composeGuards(requireCompanyManager, ctx => requireFeatureFlag(flag, ctx)),

  /**
   * Requires user to be an internal company manager with a specific feature flag
   */
  internalCompanyManagerWithFeature: (flag: FeatureFlagType): Guard =>
    composeGuards(requireInternalCompanyManager, ctx => requireFeatureFlag(flag, ctx)),

  /**
   * Requires a pass plan holder with a specific feature flag
   */
  passPlanHolderWithFeature: (flag: FeatureFlagType): Guard =>
    composeGuards(requirePassPlan, requireOnboardingCompleted, ctx => requireFeatureFlag(flag, ctx))
}

// Export individual guard functions for custom compositions
export { requireCompanyManager as companyManager }
export { requireInternalCompanyManager as internalCompanyManager }
export { requirePassPlan as passPlan }
export { requireOnboardingCompleted as onboardingCompleted }
export { requireOnboardingAccess as onboardingAccess }
export { requireFeatureFlag as featureFlag }
